/**
 * Detail panel for the selected role.
 *
 * A role is a named group: its MEMBERS (people) and its GRANTS (the resources
 * the group can reach, and at what level) are the two things an admin manages
 * here. Members resolve to human names via the admin user list.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useAdminUsers } from '@/hooks/useAdminAnalytics';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { StaleDataNotice } from '@/components/clinical';
import {
  EmptyState,
  ErrorState,
  LoadingRows,
  Panel,
  RemovableRow,
  RoleBadge,
  SectionHeader,
  errMessage,
} from './OrgUi';
import {
  useRoleMembers,
  useRoleGrants,
  useRemoveRoleMember,
  useRemoveRoleGrant,
  useDeleteRole,
  indexUsersById,
} from './useOrg';
import { RoleFormDialog, AddRoleMemberDialog, AddGrantDialog } from './OrgDialogs';
import type { Role, RoleGrant } from '@/api/endpoints/org';

export function RoleDetail({ role, onDeleted }: { role: Role; onDeleted: () => void }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [addingGrant, setAddingGrant] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold text-neutral-900">{role.name}</h2>
          {role.description && <p className="mt-1 text-sm text-neutral-500">{role.description}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            {t('common.edit', 'Edit')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600 hover:bg-red-50 hover:text-red-700"
            onClick={() => setConfirmDelete(true)}
          >
            {t('common.delete', 'Delete')}
          </Button>
        </div>
      </div>

      <MembersSection role={role} onAdd={() => setAddingMember(true)} />
      <GrantsSection role={role} onAdd={() => setAddingGrant(true)} />

      <RoleFormDialog open={editing} onOpenChange={setEditing} role={role} />
      <AddRoleMemberDialog open={addingMember} onOpenChange={setAddingMember} roleId={role.id} roleName={role.name} />
      <AddGrantDialog open={addingGrant} onOpenChange={setAddingGrant} roleId={role.id} roleName={role.name} />
      <DeleteRoleDialog open={confirmDelete} onOpenChange={setConfirmDelete} role={role} onDeleted={onDeleted} />
    </div>
  );
}

function MembersSection({ role, onAdd }: { role: Role; onAdd: () => void }) {
  const { t } = useTranslation();
  const members = useRoleMembers(role.id);
  const { data: users } = useAdminUsers();
  const remove = useRemoveRoleMember();
  const [pending, setPending] = useState<string | null>(null);

  const userById = useMemo(() => indexUsersById(users), [users]);

  return (
    <Panel className="p-4">
      <SectionHeader
        title={t('org.roleDetail.members', 'Members')}
        count={members.data?.items.length}
        action={
          <Button size="sm" variant="ghost" className="h-7 px-2 text-primary" onClick={onAdd}>
            {t('org.roleDetail.add', '+ Add')}
          </Button>
        }
      />
      <div className="mt-3">
        {/* A background refetch failing (or in flight) with cached members
            still showing must stay visible. */}
        {members.data && <StaleDataNotice isFetching={members.isFetching} isError={members.isError} className="mb-2" />}
        {members.isError && !members.data ? (
          <ErrorState error={members.error} onRetry={() => void members.refetch()} />
        ) : members.isLoading ? (
          <LoadingRows />
        ) : (members.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            title={t('org.roleDetail.noMembers', 'No members')}
            hint={t('org.roleDetail.noMembersHint', 'Add people to this role; they inherit everything the role can reach.')}
            action={
              <Button size="sm" variant="outline" onClick={onAdd}>
                {t('org.roleDetail.addMember', 'Add a member')}
              </Button>
            }
          />
        ) : (
          <div className="space-y-1">
            {members.data?.items.map((m) => (
              <RemovableRow
                key={m.user_id}
                removing={pending === m.user_id}
                onRemove={() => {
                  setPending(m.user_id);
                  remove.mutate({ id: role.id, userId: m.user_id }, { onSettled: () => setPending(null) });
                }}
              >
                <span className="truncate text-sm text-neutral-800">{userById[m.user_id] ?? m.user_id}</span>
              </RemovableRow>
            ))}
          </div>
        )}
        {remove.isError && <p className="mt-2 text-xs text-red-600">{errMessage(remove.error)}</p>}
      </div>
    </Panel>
  );
}

function GrantsSection({ role, onAdd }: { role: Role; onAdd: () => void }) {
  const { t } = useTranslation();
  const grants = useRoleGrants(role.id);
  const remove = useRemoveRoleGrant();
  const [pending, setPending] = useState<string | null>(null);

  const grantKey = (g: RoleGrant) => `${g.resource_type}:${g.resource_id}:${g.role}`;

  return (
    <Panel className="p-4">
      <SectionHeader
        title={t('org.roleDetail.accessToResources', 'Access to resources')}
        count={grants.data?.items.length}
        action={
          <Button size="sm" variant="ghost" className="h-7 px-2 text-primary" onClick={onAdd}>
            {t('org.roleDetail.grant', '+ Grant')}
          </Button>
        }
      />
      <div className="mt-3">
        {/* Same visibility rule as the members section above. */}
        {grants.data && <StaleDataNotice isFetching={grants.isFetching} isError={grants.isError} className="mb-2" />}
        {grants.isError && !grants.data ? (
          <ErrorState error={grants.error} onRetry={() => void grants.refetch()} />
        ) : grants.isLoading ? (
          <LoadingRows />
        ) : (grants.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            title={t('org.roleDetail.noGrants', 'No resource access')}
            hint={t(
              'org.roleDetail.noGrantsHint',
              'Grant this role access to a specific resource (a document, a session, a unit…) at viewer, editor, or owner level.',
            )}
            action={
              <Button size="sm" variant="outline" onClick={onAdd}>
                {t('org.roleDetail.grantAccess', 'Grant access')}
              </Button>
            }
          />
        ) : (
          <div className="space-y-1">
            {grants.data?.items.map((g) => (
              <RemovableRow
                key={grantKey(g)}
                removing={pending === grantKey(g)}
                onRemove={() => {
                  setPending(grantKey(g));
                  remove.mutate({ id: role.id, grant: g }, { onSettled: () => setPending(null) });
                }}
              >
                <div className="flex items-center gap-2">
                  <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-neutral-500">
                    {g.resource_type}
                  </span>
                  <span className="truncate font-mono text-xs text-neutral-700">{g.resource_id}</span>
                  <RoleBadge role={g.role} />
                </div>
              </RemovableRow>
            ))}
          </div>
        )}
        {grants.data?.truncated && (
          <p className="mt-2 text-xs text-neutral-400">
            {t('org.roleDetail.truncated', 'Showing the first page of grants — more exist.')}
          </p>
        )}
        {remove.isError && <p className="mt-2 text-xs text-red-600">{errMessage(remove.error)}</p>}
      </div>
    </Panel>
  );
}

function DeleteRoleDialog({
  open,
  onOpenChange,
  role,
  onDeleted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role: Role;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const del = useDeleteRole();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('org.roleDetail.deleteTitle', 'Delete {{name}}?', { name: role.name })}</DialogTitle>
          <DialogDescription>
            {t(
              'org.roleDetail.deleteDesc',
              'This removes the role and the access its members inherited through it. People keep any access granted to them directly.',
            )}
          </DialogDescription>
        </DialogHeader>
        {del.isError && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMessage(del.error)}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            className="bg-red-600 hover:bg-red-700"
            onClick={() =>
              del.mutate(role.id, {
                onSuccess: () => {
                  onOpenChange(false);
                  onDeleted();
                },
              })
            }
            disabled={del.isPending}
          >
            {del.isPending ? <Spinner className="h-4 w-4" /> : t('common.delete', 'Delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
